/**
 * Search Results Page (T153, T155, T156, T157)
 * Grouped results by entity type with counts, pagination, and URL persistence
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, User, Calendar, FileText, Mic, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearch } from '../hooks/useSearch';
import HighlightText from '../components/Search/HighlightText';

type SearchType = 'all' | 'customers' | 'appointments' | 'invoices' | 'recordings';

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryFromUrl = searchParams.get('q') || '';
  const typeFromUrl = (searchParams.get('type') || 'all') as SearchType;
  const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);

  const [query, setQuery] = useState(queryFromUrl);
  const [activeType, setActiveType] = useState<SearchType>(typeFromUrl);
  const [page, setPage] = useState(pageFromUrl);

  const { data: results, isLoading } = useSearch({
    query,
    types: activeType === 'all' ? 'all' : activeType,
    limit: 20,
    page,
  });

  // Sync URL params (T155)
  useEffect(() => {
    const params = new URLSearchParams();
    if (query) {
      params.set('q', query);
    }
    if (activeType !== 'all') {
      params.set('type', activeType);
    }
    if (page > 1) {
      params.set('page', page.toString());
    }
    setSearchParams(params, { replace: true });
  }, [query, activeType, page, setSearchParams]);

  const handleTypeChange = (type: SearchType) => {
    setActiveType(type);
    setPage(1);
  };

  const typeConfig: Array<{ key: SearchType; label: string; icon: typeof User; count: number }> = [
    { key: 'all', label: 'All', icon: Search, count: results?.totalResults || 0 },
    { key: 'customers', label: 'Customers', icon: User, count: results?.customers.count || 0 },
    {
      key: 'appointments',
      label: 'Appointments',
      icon: Calendar,
      count: results?.appointments.count || 0,
    },
    { key: 'invoices', label: 'Invoices', icon: FileText, count: results?.invoices.count || 0 },
    {
      key: 'recordings',
      label: 'Recordings',
      icon: Mic,
      count: results?.recordings.count || 0,
    },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="space-y-2">
        <h1>Search Results</h1>
        <p className="text-muted-foreground">
          {results
            ? `${results.totalResults} result${results.totalResults !== 1 ? 's' : ''} for "${query}"`
            : 'Search across your data'}
        </p>
      </div>

      {/* Search Input */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search customers, appointments, invoices..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          className="w-full pl-12 pr-4 py-3 text-base bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-background transition-colors"
          autoFocus
        />
      </div>

      {/* Type Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {typeConfig.map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            onClick={() => handleTypeChange(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeType === key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {count > 0 && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeType === key ? 'bg-primary-foreground/20' : 'bg-muted'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="claude-spinner mx-auto"></div>
        </div>
      )}

      {/* Results */}
      {!isLoading && results && (
        <div className="space-y-6">
          {/* Customers */}
          {(activeType === 'all' || activeType === 'customers') && results.customers.count > 0 && (
            <ResultSection
              title="Customers"
              icon={<User className="w-5 h-5 text-primary" />}
              count={results.customers.count}
            >
              {results.customers.results.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/customers/${c.id}`)}
                  className="w-full claude-card p-4 hover:shadow-md transition-shadow text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        <HighlightText text={c.name} query={query} />
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        <HighlightText text={c.email} query={query} />
                        {c.phone && (
                          <>
                            {' · '}
                            <HighlightText text={c.phone} query={query} />
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.tags.length > 0 && (
                        <div className="flex gap-1">
                          {c.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="claude-badge claude-badge-default text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <span className="text-xs text-muted-foreground capitalize">
                        via {c.matchField}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </ResultSection>
          )}

          {/* Appointments */}
          {(activeType === 'all' || activeType === 'appointments') &&
            results.appointments.count > 0 && (
              <ResultSection
                title="Appointments"
                icon={<Calendar className="w-5 h-5 text-primary" />}
                count={results.appointments.count}
              >
                {results.appointments.results.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate('/appointments')}
                    className="w-full claude-card p-4 hover:shadow-md transition-shadow text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">
                          <HighlightText text={a.title} query={query} />
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {a.customerName} ·{' '}
                          {new Date(a.startTime).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                        {a.snippet && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            <HighlightText text={a.snippet} query={query} />
                          </p>
                        )}
                      </div>
                      <StatusBadge status={a.status} />
                    </div>
                  </button>
                ))}
              </ResultSection>
            )}

          {/* Invoices */}
          {(activeType === 'all' || activeType === 'invoices') && results.invoices.count > 0 && (
            <ResultSection
              title="Invoices"
              icon={<FileText className="w-5 h-5 text-primary" />}
              count={results.invoices.count}
            >
              {results.invoices.results.map((inv) => (
                <button
                  key={inv.id}
                  onClick={() => navigate('/invoices')}
                  className="w-full claude-card p-4 hover:shadow-md transition-shadow text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        <HighlightText text={inv.invoiceNumber} query={query} />
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        <HighlightText text={inv.customerName} query={query} /> · $
                        {inv.total.toFixed(2)} · Due {new Date(inv.dueDate).toLocaleDateString()}
                      </p>
                    </div>
                    <StatusBadge status={inv.status} />
                  </div>
                </button>
              ))}
            </ResultSection>
          )}

          {/* Recordings */}
          {(activeType === 'all' || activeType === 'recordings') &&
            results.recordings.count > 0 && (
              <ResultSection
                title="Recordings"
                icon={<Mic className="w-5 h-5 text-primary" />}
                count={results.recordings.count}
              >
                {results.recordings.results.map((r) => (
                  <div
                    key={r.recordingId}
                    className="claude-card p-4 hover:shadow-md transition-shadow"
                  >
                    <p className="font-medium">{r.customerName}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {new Date(r.appointmentDate).toLocaleDateString()}
                    </p>
                    {r.snippet && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <HighlightText text={r.snippet} query={query} />
                      </p>
                    )}
                  </div>
                ))}
              </ResultSection>
            )}

          {/* No results */}
          {results.totalResults === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-1">No results found</h3>
              <p className="text-muted-foreground text-sm">
                Try a different search term or broaden your filters
              </p>
            </div>
          )}

          {/* Pagination (T157) */}
          {results.totalResults > 20 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="claude-button claude-button-ghost p-2 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-muted-foreground px-4">Page {page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                className="claude-button claude-button-ghost p-2"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Section wrapper
function ResultSection({
  title,
  icon,
  count,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className="text-sm text-muted-foreground">({count})</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

// Simple status badge
function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    SCHEDULED: 'claude-badge-default',
    CONFIRMED: 'claude-badge-success',
    COMPLETED: 'claude-badge-success',
    CANCELLED: 'claude-badge-error',
    NO_SHOW: 'claude-badge-error',
    DRAFT: 'claude-badge-default',
    SENT: 'claude-badge-warning',
    PAID: 'claude-badge-success',
    OVERDUE: 'claude-badge-error',
    VIEWED: 'claude-badge-warning',
  };

  return (
    <span className={`claude-badge ${colors[status] || 'claude-badge-default'}`}>
      {status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')}
    </span>
  );
}
